/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import React, { useState, useEffect } from "react";
import { bem, BEM } from "../../shared/BEM";
import { IModelInputState } from "../../shared/model/IModelInputState";
import { ModelInput, IModelInputBaseConfig } from "../../shared/model/ModelInput";
import { VALIDATION_ERROR_TYPE, IValidationErrorNumeric } from "../../shared/model/IValidationError";

/**
 * Common input field props
 */
export interface IInputCommonProps {
	/** Input label */
	label?: string;

	/** Input description */
	description?: string;

	/** Input description as HTML - has precedence over "description" */
	descriptionHTML?: string;

	/** Custom field modifiers */
	modifiers?: Array<string>;
}

/**
 * Component props
 */
export interface IInputWrapperProps<TModelInput extends ModelInput<any, any, IModelInputBaseConfig>> extends IInputCommonProps {
	/** Input field */
	input: TModelInput;

	/** Input renderer */
	render: (state: ReturnType<TModelInput["getState"]>, baseStyle: BEM) => React.ReactElement;
}

/**
 * Input field wrapper component
 *
 * @param props Props
 */
export function InputWrapper<TModelInput extends ModelInput<any, any, IModelInputBaseConfig>>(props: IInputWrapperProps<TModelInput>) {

	// Get initial state
	const [ inputState, setInputState ] = useState(() => props.input.getState());

	// Bind on change events
	useEffect(() => {

		const updateHandler = (event: IModelInputState<any, any, any>) => {
			setInputState(event);
		};

		props.input.on("update", updateHandler);
		updateHandler(props.input.getState());

		return () => {
			props.input.off("update", updateHandler);
		};

	}, [ props.input ]);

	// Get style
	const baseStyle = bem("field-input");

	const modifiers = ["type-" + inputState.type].concat(props.modifiers || []);

	if (inputState.pristine) {
		modifiers.push("pristine");
	}

	if (inputState.touched) {
		modifiers.push("touched");
	}

	if (inputState.valid) {
		modifiers.push("valid");
	} else {
		modifiers.push("invalid");
	}

	if (inputState.config.required) {
		modifiers.push("required");
	}

	const style = baseStyle.mod(modifiers, true);

	const errors = getFieldErrors(inputState);

	// Render
	return  <label className={style.className()}>
		<span className={baseStyle.el("label-wrapper").className()}>
			<span className={baseStyle.el("label").className()}>
				{ /* tslint:disable-next-line: max-line-length */ }
				{props.label || inputState.config.label}{inputState.config.required ? <span className={baseStyle.el("label-required").className()}>*</span> : null}
			</span>
			{props.descriptionHTML
				? <span className={baseStyle.el("description").className()} dangerouslySetInnerHTML={{ __html: props.descriptionHTML }}></span>
			: props.description || inputState.config.description
				? <span className={baseStyle.el("description").className()}>{props.description || inputState.config.description}</span>
			: null}
		</span>
		<span className={baseStyle.el("field-wrapper").className()}>
			<span className={baseStyle.el("field").className()}>
				{props.render((inputState as any) as ReturnType<TModelInput["getState"]>, baseStyle)}
			</span>
			{errors.length > 0 ?
			<ul className={baseStyle.el("errors").className()}>
				{errors}
			</ul>
			: null}
		</span>
	</label>;

}

/**
 * Returns elements for field error messages
 *
 * @param inputState Input field state
 */
function getFieldErrors(inputState: IModelInputState<any, any, any>) {

	const elements = [];

	if (inputState.validationErrors.length > 0) {
		for (let i = 0; i < inputState.validationErrors.length; i++) {

			const err = inputState.validationErrors[i];

			switch (err.type) {

				case VALIDATION_ERROR_TYPE.INVALID_NUMBER:
					elements.push(<li key={i}>Zadaná hodnota musí být platné číslo.</li>);
					break;

				case VALIDATION_ERROR_TYPE.INVALID_OPTION:
					elements.push(<li key={i}>Zadaná hodnota není v seznamu povolených možností.</li>);
					break;

				case VALIDATION_ERROR_TYPE.VALUE_MIN:
					elements.push(<li key={i}>Zadaná hodnota musí být větší nebo rovna číslu {(err as IValidationErrorNumeric).min}.</li>);
					break;

				case VALIDATION_ERROR_TYPE.VALUE_MAX:
					elements.push(<li key={i}>Zadaná hodnota musí být menší nebo rovna číslu {(err as IValidationErrorNumeric).max}.</li>);
					break;

			}

		}
	}

	return elements;

}
